// Maps between the API's ID/enum-based record shapes and the display-friendly
// name/label shapes the page components already know how to render and edit.

export const fmtSince = (iso) => new Date(iso).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

const stageDisplay = { NEW_LEAD: 'New lead', QUALIFIED: 'Qualified', PROPOSAL: 'Proposal', NEGOTIATION: 'Negotiation', WON: 'Won', LOST: 'Lost' };
const partnerStatusDisplay = { ACTIVE: 'Active', AT_RISK: 'At risk', ONBOARDING: 'Onboarding' };

export function fromApiAccount(a) {
  return {
    id: a.id, name: a.name, industry: a.industry || '', size: a.size || '—',
    revenueAmount: a.revenueAmount || 0, revenueUnit: a.revenueUnit || 'Million',
    hq: a.headquarters || '—', hqCountry: a.hqCountry || '', hqState: a.hqState || '', hqCity: a.hqCity || '',
    website: a.website || '—', stage: a.stage, pipelineStage: a.pipelineStage,
    owner: a.ownerId, health: a.health, source: a.source, since: fmtSince(a.createdAt),
    tone: a.tone || '#697b96', phone: a.phone || '—', notes: a.notes || '',
    contacts: (a.contacts || []).map(fromApiContact),
  };
}

export function toApiAccount(form) {
  return {
    name: form.name, industry: form.industry || null, website: form.website || null,
    headquarters: form.hq || null, hqCountry: form.hqCountry || null, hqState: form.hqState || null, hqCity: form.hqCity || null,
    phone: form.phone || null, notes: form.notes || null, tone: form.tone || null,
    stage: form.stage || 'Lead', pipelineStage: form.pipelineStage || 'Proposal', health: Number(form.health) || 0,
    source: form.source || 'Internal', size: form.size || null, revenueAmount: Number(form.revenueAmount) || 0,
    revenueUnit: form.revenueUnit || 'Million', ownerId: form.owner,
  };
}

export function fromApiContact(c) {
  return { id: c.id, accountId: c.accountId, name: c.name, role: c.role || '', email: c.email || '', phone: c.phone || '', primary: !!c.primary };
}

export function toApiContact(form) {
  return { name: form.name, role: form.role || null, email: form.email || null, phone: form.phone || null, primary: !!form.primary };
}

export function fromApiDeal(d) {
  return {
    id: d.id, accountId: d.accountId, account: d.account?.name || '', contactId: d.contactId || null, contact: d.contact?.name || '—',
    owner: d.ownerId, products: (d.products || []).map((dp) => dp.product?.name).filter(Boolean),
    value: Number(d.value), stage: stageDisplay[d.stage] || d.stage, close: (d.closeDate || '').slice(0, 10),
    source: d.source, probability: d.probability, tone: d.account?.tone || '#697b96',
    notes: Array.isArray(d.notes) ? d.notes : [],
  };
}

// `context` supplies the currently-loaded accounts/contacts/products lists so
// display names on the form (e.g. `form.account`, `form.products` as names)
// can be resolved back to the ids the API expects.
export function toApiDeal(form, { accounts, contacts, products }) {
  const accountId = form.accountId || accounts.find((a) => a.name === form.account)?.id;
  const account = accounts.find((a) => a.id === accountId);
  const contact = contacts.find((c) => c.accountId === accountId && c.name === form.contact);
  const productIds = (form.products || []).map((name) => products.find((p) => p.name === name)?.id).filter(Boolean);
  return {
    accountId,
    contactId: contact?.id ?? null,
    productIds,
    ownerId: form.owner,
    source: form.source ?? account?.source ?? 'Internal',
    stage: form.stage,
    value: Number(form.value) || 0,
    probability: form.probability === undefined || form.probability === '' ? 65 : Number(form.probability),
    closeDate: form.close,
    notes: form.notes,
  };
}

export function fromApiPartner(p) {
  return {
    id: p.id, name: p.name, tier: p.tier, owner: p.managerId, health: p.health,
    status: partnerStatusDisplay[p.status] || p.status, commissionRate: Number(p.commissionRate),
    since: fmtSince(p.createdAt), color: p.color || '#697b96',
    contacts: [{ name: p.contactName || '—', role: p.contactRole || 'Primary contact', email: p.contactEmail || '—' }],
  };
}

export function toApiPartner(form) {
  return {
    name: form.name, tier: form.tier, commissionRate: Number(form.commissionRate) || 0,
    managerId: form.owner, status: form.status, health: Number(form.health) || 0,
    color: form.color || null, contactName: form.contactName || null, contactRole: form.contactRole || null, contactEmail: form.contactEmail || null,
  };
}

export function fromApiTask(t) {
  return { id: t.id, title: t.title, type: t.type, relatedType: t.relatedType || '', relatedId: t.relatedId || '', owner: t.ownerId, priority: t.priority, status: t.status, due: (t.dueAt || '').slice(0, 10) };
}

export function relatedName(task, accounts, partners) {
  if (task.relatedType === 'Account') return accounts.find((a) => a.id === task.relatedId)?.name || '—';
  if (task.relatedType === 'Partner') return partners.find((p) => p.id === task.relatedId)?.name || '—';
  return '—';
}

export function toApiTask(form) {
  return { title: form.title, type: form.type, relatedType: form.relatedType || null, relatedId: form.relatedId || null, ownerId: form.owner, priority: form.priority, status: form.status || 'Open', dueAt: form.due };
}

const roleDisplay = { ADMINISTRATOR: 'Administrator', SALES_LEAD: 'Sales Lead', ACCOUNT_EXECUTIVE: 'Account Executive', PARTNER_MANAGER: 'Partner Manager', FINANCE: 'Finance', TEAM_MEMBER: 'Team member' };

export function fromApiTeamMember(u) {
  return { id: u.id, name: u.name, email: u.email, role: roleDisplay[u.role] || u.role, color: u.color };
}

export function toApiTeamMember(form) {
  return { name: form.name, email: form.email, role: form.role, color: form.color };
}

export function fromApiWorkspace(w) {
  return {
    name: w.name,
    currency: w.currency,
    notifications: w.settings
      ? { dealStage: w.settings.dealStage, overdue: w.settings.overdue, taskDue: w.settings.taskDue, weeklyDigest: w.settings.weeklyDigest }
      : { dealStage: true, overdue: true, taskDue: true, weeklyDigest: false },
  };
}

const notificationTone = { DEAL_STAGE: 'info', TASK_DUE: 'warn', TASK_OVERDUE: 'fail' };

export function fromApiNotification(n) {
  return {
    id: n.id, unread: !n.read, tone: notificationTone[n.type] || 'info',
    title: n.title, body: n.body,
    time: new Date(n.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
  };
}

export function fromApiProduct(p) {
  return { id: p.id, name: p.name, acronym: p.acronym, description: p.description, category: p.category, pricing: p.pricing, status: p.status.charAt(0) + p.status.slice(1).toLowerCase().replace('_', ' '), url: p.url, logo: p.logo, color: p.color, accounts: p.proposalAccounts, owner: p.ownerName, audience: p.audience, version: p.version, arr: 0 };
}

export function toApiProduct(form) {
  return { name: form.name, acronym: form.acronym, description: form.description, category: form.category, pricing: form.pricing, status: form.status, url: form.url, logo: form.logo, color: form.color, accounts: Number(form.accounts) || 0, owner: form.owner, audience: form.audience, version: form.version };
}
